import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, Sparkles } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { cn } from '@ui/lib/utils';

import { canAnimate } from './Presence';

export type WritingMode = 'ai' | 'plain';

export interface ContinueButtonProps {
  /** The way offered on the main part; the other one sits under the caret. */
  main: WritingMode;
  onContinue: (mode: WritingMode) => void;
  /** The other way was picked under the caret: it becomes the main part, nothing is submitted. */
  onChoose: (mode: WritingMode) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
}

/** A split button whose caret opens the other way to continue, attached above it so the footer keeps its height. */
const ContinueButton = ({
  main,
  onContinue,
  onChoose,
  open,
  onOpenChange,
  disabled,
}: ContinueButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const other: WritingMode = main === 'ai' ? 'plain' : 'ai';
  const labelRef = useRef<HTMLSpanElement>(null);
  // The label fades in only when the way changes, not each time the button mounts, e.g. on a tab switch.
  const mountedMain = useRef(main);
  const hasSwitched = useRef(false);
  if (main !== mountedMain.current) hasSwitched.current = true;
  // Held as a number so the width can glide when the main way changes; `auto` cannot be transitioned.
  const [labelWidth, setLabelWidth] = useState<number>();

  useLayoutEffect(() => {
    if (labelRef.current) setLabelWidth(labelRef.current.offsetWidth);
  }, [main, t]);

  // Kept on screen while it animates out, showing the way it offered when it opened.
  const [menuMode, setMenuMode] = useState<WritingMode>();
  useEffect(() => {
    if (open) {
      setMenuMode(other);
      return;
    }
    if (!canAnimate()) setMenuMode(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `other` flips the moment a way is picked; the menu keeps the one it opened with.
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent): void => {
      if (event.target instanceof Node && !ref.current?.contains(event.target)) onOpenChange(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open, onOpenChange]);

  const label = (mode: WritingMode): React.ReactNode =>
    mode === 'ai' ? (
      <>
        <Sparkles aria-hidden />
        {t('phillReport.draftWithAi')}
      </>
    ) : (
      t('phillReport.writeYourself')
    );

  return (
    <div ref={ref} className="relative inline-flex">
      <div className="flex">
        <Button
          className={cn('flex-1 rounded-r-none', open && 'rounded-tl-none')}
          onClick={() => onContinue(main)}
          disabled={disabled}
        >
          <span
            className="flex justify-center overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none"
            style={{ width: labelWidth }}
          >
            <span
              ref={labelRef}
              key={main}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap',
                hasSwitched.current && 'animate-in fade-in-0 duration-200 motion-reduce:animate-none',
              )}
            >
              {/* Writing it yourself happens in the form itself, so its main part sends the report. */}
              {main === 'plain' ? t('phillReport.submit') : label(main)}
            </span>
          </span>
        </Button>
        <Button
          size="icon"
          className={cn('rounded-l-none border-l-primary-foreground/25', open && 'rounded-tr-none')}
          aria-expanded={open}
          aria-label={t('phillReport.otherWay')}
          onClick={() => onOpenChange(!open)}
          disabled={disabled}
        >
          <ChevronUp className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
        </Button>
      </div>
      {menuMode ? (
        <Button
          className={cn(
            'absolute right-0 bottom-full left-0 z-10 origin-bottom justify-start rounded-b-none border-b border-b-primary-foreground/25 shadow-md duration-150 motion-reduce:animate-none',
            open
              ? 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 ease-out'
              : 'pointer-events-none animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-2 fill-mode-forwards ease-in',
          )}
          onAnimationEnd={() => {
            if (!open) setMenuMode(undefined);
          }}
          onClick={() => {
            onChoose(menuMode);
            onOpenChange(false);
          }}
          tabIndex={open ? undefined : -1}
        >
          {label(menuMode)}
        </Button>
      ) : null}
    </div>
  );
};

export default ContinueButton;
